import { useEffect, useRef, useState } from 'react';
import { Copy, Trash2 } from 'lucide-react';
import {
  getButtonSnapshots,
  clearButtonSnapshots,
  subscribeButtonSnapshots,
  type ButtonSnapshot,
} from '../lib/buttonDiagnosticBuffer';

export function ButtonGeometryDiagnostic() {
  const [snapshots, setSnapshots] = useState<ButtonSnapshot[]>(getButtonSnapshots());
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = subscribeButtonSnapshots(() => {
      setSnapshots(getButtonSnapshots());
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [snapshots.length]);

  const handleCopy = async () => {
    const payload = {
      capturedAt: new Date().toISOString(),
      totalSnapshots: snapshots.length,
      snapshots,
    };
    const text = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch {
      setCopyStatus('failed');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  const handleClear = () => {
    clearButtonSnapshots();
  };

  return (
    <div
      style={{
        width: '420px',
        maxHeight: '60vh',
        background: '#0a0a0f',
        border: '1px solid rgba(6, 182, 212, 0.4)',
        borderRadius: '8px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        fontSize: '11px',
        color: '#e4e4e7',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: 'rgba(6, 182, 212, 0.08)',
          borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontWeight: 600, color: '#06b6d4' }}>
            Phase F.1.3 Button Diagnostic
          </span>
          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
            ({snapshots.length} snapshots)
          </span>
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            onClick={handleCopy}
            title="Copy all as JSON"
            style={{
              background: copyStatus === 'copied'
                ? 'rgba(34, 197, 94, 0.2)'
                : 'rgba(6, 182, 212, 0.15)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: copyStatus === 'copied' ? '#22c55e' : '#06b6d4',
              fontSize: '10px',
            }}
          >
            <Copy size={11} />
            {copyStatus === 'copied' ? 'Copied!' : copyStatus === 'failed' ? 'Failed' : 'Copy All'}
          </button>
          <button
            onClick={handleClear}
            title="Clear snapshots"
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#ef4444',
              fontSize: '10px',
            }}
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}
      >
        {snapshots.length === 0 ? (
          <div style={{ color: 'rgba(255, 255, 255, 0.4)', textAlign: 'center', padding: '20px 0' }}>
            No snapshots yet. Create or modify a button to capture geometry.
          </div>
        ) : (
          snapshots.map((snap, idx) => (
            <SnapshotCard key={`${snap.timestamp}-${idx}`} snapshot={snap} index={idx} />
          ))
        )}
      </div>
    </div>
  );
}

function SnapshotCard({ snapshot, index }: { snapshot: ButtonSnapshot; index: number }) {
  const labelInkOffsetFromBgCenter =
    snapshot.bgWidth !== undefined &&
    snapshot.bgLeft !== undefined &&
    snapshot.labelInkLeft !== undefined &&
    snapshot.labelInkRight !== undefined
      ? (snapshot.labelInkLeft + snapshot.labelInkRight) / 2 -
        (snapshot.bgLeft + snapshot.bgWidth / 2)
      : null;

  const phaseColor =
    snapshot.phase === 'factory-pre-fit' ? '#facc15' :
    snapshot.phase === 'factory-post-fit' ? (snapshot.fitChanged ? '#ef4444' : '#22c55e') :
    snapshot.phase === 'sync-update' ? '#06b6d4' :
    '#a78bfa';

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderLeft: `3px solid ${phaseColor}`,
        borderRadius: '4px',
        padding: '6px 8px',
        fontSize: '10px',
        lineHeight: 1.5,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
        <span style={{ color: phaseColor, fontWeight: 600 }}>
          #{index + 1} · {snapshot.phase}
        </span>
        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
          {new Date(snapshot.timestamp).toLocaleTimeString('en-US', { hour12: false }).split(' ')[0]}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', color: 'rgba(255, 255, 255, 0.7)' }}>
        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>text:</span>
        <span style={{ color: '#fde68a' }}>"{snapshot.text}" ({snapshot.textLen} chars)</span>

        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>font:</span>
        <span>
          {snapshot.fontFamily} / {String(snapshot.fontWeight)} / {snapshot.fontSize}px
        </span>

        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>paddingX:</span>
        <span>{snapshot.paddingX ?? 'undefined'}</span>

        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>stored w*h:</span>
        <span>{snapshot.storedWidth} x {snapshot.storedHeight}</span>

        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>bg w*h:</span>
        <span>{fmt(snapshot.bgWidth)} x {fmt(snapshot.bgHeight)}</span>

        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>bg pos:</span>
        <span>L={fmt(snapshot.bgLeft)}, T={fmt(snapshot.bgTop)}</span>

        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>label w*h:</span>
        <span>{fmt(snapshot.labelWidth)} x {fmt(snapshot.labelHeight)}</span>

        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>label pos:</span>
        <span>L={fmt(snapshot.labelLeft)}, T={fmt(snapshot.labelTop)}</span>

        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>label ink:</span>
        <span>L={fmt(snapshot.labelInkLeft)} -&gt; R={fmt(snapshot.labelInkRight)}</span>

        <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>group:</span>
        <span>
          L={fmt(snapshot.groupLeft)}, T={fmt(snapshot.groupTop)}, w={fmt(snapshot.groupWidth)}, h={fmt(snapshot.groupHeight)}
        </span>

        {labelInkOffsetFromBgCenter !== null && (
          <>
            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>ink offset:</span>
            <span style={{ color: Math.abs(labelInkOffsetFromBgCenter) > 1 ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
              {labelInkOffsetFromBgCenter > 0 ? '+' : ''}{labelInkOffsetFromBgCenter.toFixed(2)}px from bg center
            </span>
          </>
        )}

        {snapshot.fitChanged !== undefined && (
          <>
            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>fit:</span>
            <span style={{ color: snapshot.fitChanged ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
              {snapshot.fitChanged ? `GREW to ${snapshot.fitNewWidth}` : 'no change'}
            </span>
          </>
        )}

        {snapshot.note && (
          <>
            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>note:</span>
            <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic' }}>{snapshot.note}</span>
          </>
        )}
      </div>
    </div>
  );
}

function fmt(value: number | undefined): string {
  if (value === undefined || value === null) return 'undef';
  return value.toFixed(2);
}
